/**
 * ExerciseGenerator — Exercices PDF (Carbon Design System)
 */

import { useState } from 'react';
import { Pen } from '@carbon/icons-react';
import {
  FileUpload,
  LanguageSelector,
  AIProcessingCard,
  AiGeneratorPageLayout,
  AiGeneratorFormCard,
  AiFormStep,
  AiGenerateButton,
  AiSuccessBanner,
} from '@sphere/learning/ui';
import { useGenerateExercisePdf, useFileValidation } from '@sphere/learning/data-access';
import type { SupportedLanguage } from '@sphere/ai/domain';
import { handleAIError } from '@sphere/ai/data-access';
import { downloadBlob } from '@sphere/shared/util';
import tw from './ExerciseGenerator.tailwind.module.scss';

const EXERCISE_TYPES = [
  'Questions de compréhension et d\'analyse',
  'Réflexion et pensée critique',
  'Applications pratiques des concepts',
  'Synthèse et exercices d\'écriture',
];

export default function ExerciseGenerator() {
  const [language, setLanguage] = useState<SupportedLanguage>('fr' as SupportedLanguage);
  const [isSuccess, setIsSuccess] = useState(false);
  const [lastFileName, setLastFileName] = useState<string>('');
  const [lastBlob, setLastBlob] = useState<Blob | null>(null);

  const { file, validation, setFile } = useFileValidation();
  const generateExercisePdf = useGenerateExercisePdf();

  const handleGenerate = async () => {
    if (!file || !validation.isValid) return;
    try {
      const result = await generateExercisePdf.mutateAsync({ file, lang: language });
      downloadBlob(result.blob, result.filename);
      setLastBlob(result.blob);
      setLastFileName(result.filename);
      setIsSuccess(true);
    } catch (error) {
      console.error('Exercise PDF generation failed:', error);
    }
  };

  const handleDownload = () => {
    if (lastBlob && lastFileName) {
      downloadBlob(lastBlob, lastFileName);
    }
  };

  const handleNewGeneration = () => {
    setFile(null);
    setIsSuccess(false);
    setLastFileName('');
    setLastBlob(null);
  };

  return (
    <AiGeneratorPageLayout
      icon={Pen}
      title="Générateur d'Exercices IA (PDF)"
      description="Transformez vos documents en exercices structurés prêts à télécharger. Un outil professionnel pour les enseignants et les apprenants autonomes."
    >
        {isSuccess && (
          <AiSuccessBanner
            title="Exercices générés avec succès"
            fileName={lastFileName}
            onDownload={handleDownload}
            onNewGeneration={handleNewGeneration}
          />
        )}

        <AiGeneratorFormCard title="Créer des Exercices" icon={Pen}>
          <AiFormStep step={1} label="Sélectionnez votre document">
            <FileUpload
              file={file}
              onFileChange={setFile}
              disabled={generateExercisePdf.isPending}
            />
            {validation.error && (
              <p className={`cds--form-requirement ${tw.u1}`}>{validation.error}</p>
            )}
          </AiFormStep>
          <AiFormStep step={2} label="Choisissez la langue">
            <LanguageSelector
              value={language}
              onChange={setLanguage}
              disabled={generateExercisePdf.isPending}
            />
          </AiFormStep>
          <AiFormStep step={3} label="Types d'exercices inclus">
            <ul className={`cds--type-body-compact-01 ${tw.u2}`}>
              {EXERCISE_TYPES.map((item, i) => (
                <li key={i}>{item}</li>
              ))}
            </ul>
          </AiFormStep>
          <AiFormStep step={4} label="Générez et téléchargez">
            <AiGenerateButton
              onClick={handleGenerate}
              disabled={
                !file || !validation.isValid || generateExercisePdf.isPending
              }
              isLoading={generateExercisePdf.isPending}
              label="Générer et Télécharger le PDF"
              loadingLabel="Génération..."
              icon={Pen}
            />
          </AiFormStep>
        </AiGeneratorFormCard>

        {generateExercisePdf.isPending && (
          <div className={tw.u3}>
            <AIProcessingCard
              isLoading
              error={null}
              title="Génération des exercices..."
              description="L'IA analyse votre contenu et crée des exercices structurés..."
            />
          </div>
        )}

        {generateExercisePdf.error && (
          <div className={tw.u3}>
            <AIProcessingCard
              isLoading={false}
              error={handleAIError(generateExercisePdf.error)}
              title="Erreur de génération"
              onRetry={handleGenerate}
            />
          </div>
        )}
    </AiGeneratorPageLayout>
  );
}
